import { SectionHeader, SectionWrap } from "@/components/Section";
import { AlertTriangle, Activity, Network, Clock } from "lucide-react";

const POINTS = [
  {
    icon: Activity,
    title: "Neural activity is dynamic",
    body: "Patterns vary across people and shift over time. Static models miss the signal beneath the noise.",
  },
  {
    icon: Network,
    title: "Databases don't generalize",
    body: "Existing neural datasets are narrow. They struggle to transfer across individuals and conditions.",
  },
  {
    icon: AlertTriangle,
    title: "Rigid interfaces fail",
    body: "Traditional probes can trigger tissue response that erodes signal fidelity in the long run.",
  },
  {
    icon: Clock,
    title: "Real-time data is the bottleneck",
    body: "Without continuous, scalable signal access, adaptive feedback cannot meaningfully close the loop.",
  },
];

export function ProblemSection() {
  return (
    <SectionWrap id="problem">
      <SectionHeader
        num="01"
        kicker="The Problem"
        title="Brain disorders are complex. Current tools are not enough."
        sub="Neurological and psychiatric conditions are driven by individualized, dynamic neural activity. Yet today's tools often provide incomplete maps, limited personalization, and unstable long-term signal quality."
      />

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/5">
        {POINTS.map(({ icon: Icon, title, body }, i) => (
          <div
            key={i}
            className="reveal group bg-[#07111F] hover:bg-[#0B1628] transition-colors p-8 lg:p-10 flex flex-col gap-4"
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <div className="flex items-center justify-between">
              <div className="h-11 w-11 rounded-xl border border-white/10 grid place-items-center text-[#38BDF8] group-hover:border-[#38BDF8]/40 transition-colors">
                <Icon size={20} strokeWidth={1.6} />
              </div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-[#475569]">
                0{i + 1}
              </span>
            </div>
            <h3 className="font-display text-white text-[22px] font-medium">
              {title}
            </h3>
            <p className="text-[#94A3B8] text-[15px] leading-relaxed">{body}</p>
          </div>
        ))}
      </div>

      <p className="reveal mt-10 text-[#64748B] text-[14px] max-w-2xl">
        Neurobolt approaches these as an engineering problem: capture better
        signals, model the circuit, and close the loop with adaptive feedback.
      </p>
    </SectionWrap>
  );
}
